import { composeOptionTicker, expirationTsMsToExpiry } from '@/features/chart/lib/utils/option-util'
import { getTickerBaseCurrency } from '@/features/order/order-form/lib/parse-instrument-symbol'
import { formatLocalizedDate } from '@/lib/utils/date-locale'
import { Instrument, OptionType } from '@/types/generated/common/types'

/**
 * Parse option instrument name into components
 * Supports both formats:
 * - "BTC-28JUN24-70000-C" → { ticker: "BTC", baseCurrency: "BTC", isUSDCQuoted: false, ... }
 * - "BTC_USDC-28JUN24-70000-C" → { ticker: "BTC_USDC", baseCurrency: "BTC", isUSDCQuoted: true, ... }
 *
 * isUSDCQuoted: true means prices are already in USDC, no need to multiply by underlying price
 *
 * ⚠️ Deribit-only regex — do not call this directly from feature code. Any venue with a
 * different option symbol format (e.g. Bybit) silently returns null here, which has already
 * caused real bugs: wrong ticker labels, dropped Position Roll targets, undefined
 * strike/type on SelectedOption. Use `resolveOptionInstrument(symbol, instrument)` below
 * instead — it prefers the exchange's own structured Instrument metadata and only falls
 * back to this regex when that metadata is unavailable. This function should only be
 * called from inside `resolveOptionInstrument` itself.
 */
export function parseOptionInstrument(instrumentName: string): {
  ticker: string
  baseCurrency: string
  isUSDCQuoted: boolean
  expiry: string
  strike: number
  type: 'C' | 'P'
} | null {
  // Use regex to handle both BTC-... and BTC_USDC-... formats
  // Support integer strikes (BTC: 70000), Deribit decimal format (XRP_USDC: 1d35), and dot notation (1.35)
  const match = instrumentName.match(
    /^([A-Z]+(?:_[A-Z]+)?)-(\d{1,2}[A-Z]{3}\d{2})-([0-9d.]+)-([CP])$/i
  )

  if (!match) return null

  const [, ticker, expiry, strikeStr, type] = match
  const strike = parseFloat(strikeStr.replace(/d/i, '.'))

  if (isNaN(strike)) return null

  const upperType = type.toUpperCase()
  if (upperType !== 'C' && upperType !== 'P') return null

  // Determine if USDC-quoted (e.g., BTC_USDC, ETH_USDC)
  const isUSDCQuoted = ticker.includes('_USDC')
  const baseCurrency = getTickerBaseCurrency(ticker)

  return { ticker, baseCurrency, isUSDCQuoted, expiry, strike, type: upperType as 'C' | 'P' }
}

/**
 * Same shape as `parseOptionInstrument`, but prefers the exchange's own structured
 * instrument metadata (strikePrice/expirationTsMs/optionType) over regex-parsing
 * `instrumentName` — the regex above only recognizes Deribit-style symbols, so any
 * option on a venue with a different symbol format silently fails to parse and the
 * Strike/Expiry dropdowns render nothing. Real metadata works regardless of symbol
 * format. Falls back to `parseOptionInstrument(instrumentName)` when `instrument` is
 * missing or lacks the option fields (e.g. not found in the current instrument list).
 */
export function resolveOptionInstrument(
  instrumentName: string,
  instrument?: Instrument
): ReturnType<typeof parseOptionInstrument> {
  if (
    instrument?.strikePrice != null &&
    instrument?.expirationTsMs != null &&
    instrument?.optionType != null &&
    instrument.optionType !== OptionType.OPTION_TYPE_UNSPECIFIED
  ) {
    const strike = parseFloat(instrument.strikePrice)
    if (!isNaN(strike)) {
      const expiry = expirationTsMsToExpiry(instrument.expirationTsMs)
      const type = instrument.optionType === OptionType.OPTION_TYPE_CALL ? 'C' : 'P'
      // Prefer structured currency metadata. Bybit-style symbols may append their
      // quote after the option side (e.g. `...-C-USDT`), so deriving this family
      // from a symbol prefix loses the quote and breaks later kind transitions.
      const symbolParsed = parseOptionInstrument(instrument.symbol)
      const structuredBase = instrument.baseCurrency?.toUpperCase()
      const structuredQuote = instrument.quoteCurrency?.toUpperCase()
      const ticker = structuredBase
        ? composeOptionTicker(structuredBase, structuredQuote)
        : symbolParsed?.ticker
      if (!ticker) return null
      const isUSDCQuoted =
        structuredQuote === 'USDC' ||
        (structuredQuote == null && symbolParsed?.isUSDCQuoted === true)
      const baseCurrency = structuredBase || getTickerBaseCurrency(ticker)
      return { ticker, baseCurrency, isUSDCQuoted, expiry, strike, type }
    }
  }
  return parseOptionInstrument(instrumentName)
}

/**
 * Build option instrument name from components
 */
export function buildOptionInstrument(
  ticker: string,
  expiry: string,
  strike: number,
  type: 'C' | 'P'
): string {
  return `${ticker}-${expiry}-${strike}-${type}`
}

/**
 * Get available strikes for a ticker and expiry from instruments list
 */
export function getAvailableStrikes(
  instruments: Instrument[],
  ticker: string,
  expiry: string
): number[] {
  const strikes = new Set<number>()

  instruments.forEach((inst) => {
    const parsed = resolveOptionInstrument(inst.symbol, inst)
    if (parsed && parsed.ticker === ticker && parsed.expiry === expiry) {
      strikes.add(parsed.strike)
    }
  })

  return [...strikes].sort((a, b) => a - b)
}

/**
 * Get available expiries for a ticker from instruments list
 */
export function getAvailableExpiries(instruments: Instrument[], ticker: string): string[] {
  const expiries = new Set<string>()

  instruments.forEach((inst) => {
    const parsed = resolveOptionInstrument(inst.symbol, inst)
    if (parsed && parsed.ticker === ticker) {
      expiries.add(parsed.expiry)
    }
  })

  // Sort by date (parse expiry format like "28JUN24")
  return [...expiries].sort((a, b) => {
    const parseExpiry = (exp: string) => {
      // Format: D?DMMMY (e.g., 28JUN24 or 2APR26 — day can be 1 or 2 digits)
      const dayLen = /^\d{2}[A-Z]/.test(exp) ? 2 : 1
      const day = parseInt(exp.slice(0, dayLen), 10)
      const monthStr = exp.slice(dayLen, dayLen + 3)
      const year = 2000 + parseInt(exp.slice(dayLen + 3), 10)
      const months: Record<string, number> = {
        JAN: 0,
        FEB: 1,
        MAR: 2,
        APR: 3,
        MAY: 4,
        JUN: 5,
        JUL: 6,
        AUG: 7,
        SEP: 8,
        OCT: 9,
        NOV: 10,
        DEC: 11,
      }
      return new Date(year, months[monthStr] || 0, day).getTime()
    }
    return parseExpiry(a) - parseExpiry(b)
  })
}

/**
 * Convert ask/bid side to buy/sell
 * ask = user is buying at ask price (Long)
 * bid = user is selling at bid price (Short)
 */
export function sideToDirection(side: 'ask' | 'bid'): 'buy' | 'sell' {
  return side === 'ask' ? 'buy' : 'sell'
}

/**
 * Convert buy/sell direction to ask/bid side
 */
export function directionToSide(direction: 'buy' | 'sell'): 'ask' | 'bid' {
  return direction === 'buy' ? 'ask' : 'bid'
}

/**
 * Format expiry string like "28JUN24" into a localized date.
 */
export function formatExpiryDate(expiry: string | null | undefined, locale?: string): string {
  if (!expiry || expiry.length < 5) return expiry || ''
  // Day can be 1 or 2 digits (e.g., "28JUN24" or "2APR26")
  const dayLen = /^\d{2}[A-Z]/i.test(expiry) ? 2 : 1
  const day = parseInt(expiry.slice(0, dayLen), 10)
  const monthStr = expiry.slice(dayLen, dayLen + 3).toUpperCase()
  const yearSuffix = expiry.slice(dayLen + 3)
  const year = yearSuffix.length === 2 ? 2000 + parseInt(yearSuffix, 10) : parseInt(yearSuffix, 10)

  const months: Record<string, number> = {
    JAN: 0,
    FEB: 1,
    MAR: 2,
    APR: 3,
    MAY: 4,
    JUN: 5,
    JUL: 6,
    AUG: 7,
    SEP: 8,
    OCT: 9,
    NOV: 10,
    DEC: 11,
  }
  const month = months[monthStr]

  if (!Number.isFinite(day) || !Number.isFinite(year) || month === undefined) return expiry

  return formatLocalizedDate(new Date(year, month, day), locale, 'expiryFull')
}
